import React from 'react';
import { Box, Stack, Typography, alpha } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { STATUS_COLORS, CHART_INK } from './chartUtils';

const STATUS_META = {
    COMPLETED: { label: 'הצליח', color: STATUS_COLORS.good, icon: CheckCircleIcon },
    FAILED: { label: 'נכשל', color: STATUS_COLORS.critical, icon: CancelIcon },
    IN_PROGRESS: { label: 'בתהליך', color: STATUS_COLORS.warning, icon: HourglassEmptyIcon },
};

const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' });
};

// One row per process showing its single most recent run — "is it running,
// and did it succeed" is the more actionable at-a-glance question for a
// monitoring dashboard than a raw chronological activity feed (see
// automationStatsService.getLastRun on the backend).
const ProcessHealthList = ({ projects }) => {
    if (!projects?.length) {
        return <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>אין תהליכים להצגה</Typography>;
    }

    return (
        <Stack gap={1.5}>
            {projects.map((project) => {
                const meta = project.lastRun ? STATUS_META[project.lastRun.status] : null;
                const Icon = meta?.icon || HourglassEmptyIcon;
                const color = meta?.color || CHART_INK.muted;

                return (
                    <Box
                        key={project.key}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1.5,
                            p: 1.5,
                            borderRadius: 3,
                            border: '1px solid rgba(11,11,11,0.08)',
                        }}
                    >
                        <Stack direction="row" alignItems="center" gap={1.25} sx={{ minWidth: 0 }}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    bgcolor: alpha(color, 0.14),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <Icon sx={{ color, fontSize: 19 }} />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={700}>{project.label}</Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    {project.lastRun
                                        ? `${meta?.label || project.lastRun.status}${project.lastRun.referenceId ? ` · #${project.lastRun.referenceId}` : ''}`
                                        : 'אין הרצות בטווח שנבחר'}
                                </Typography>
                            </Box>
                        </Stack>
                        {project.lastRun && (
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                                {formatTimestamp(project.lastRun.timestamp)}
                            </Typography>
                        )}
                    </Box>
                );
            })}
        </Stack>
    );
};

export default ProcessHealthList;
